import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconCirclesRelation, IconBook, IconWaveSine, IconCompass, IconAward,
  IconLayoutDashboard, IconMail, IconChartArrowsVertical, IconArrowRight, IconPlug,
  IconCreditCard, IconHistory, IconUserCog, IconX, IconLoader2, IconReceipt, IconCircleCheck,
  IconUsers,
} from "@tabler/icons-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { visibleModules } from "@/lib/rbac";

const MODULES = [
  { id: "circulo", to: "/app/circulo", icon: <IconCirclesRelation size={22} />, active: true },
  { id: "sintonia", to: "/app/sintonia", icon: <IconWaveSine size={22} />, active: true },
  { id: "aula", to: "/app/aula", icon: <IconBook size={22} />, active: true },
  { id: "brujula", to: "/app/brujula", icon: <IconCompass size={22} />, active: true },
  { id: "sello", to: "/app/sello", icon: <IconAward size={22} />, active: true },
];

const ROLE_LABELS = {
  es: { psicologa: "Psicóloga", docente: "Docente", sombra: "Maestra Sombra", familia: "Familia", escuela: "Escuela" },
  en: { psicologa: "Psychologist", docente: "Teacher", sombra: "Shadow Teacher", familia: "Family", escuela: "School" },
};

const INSTITUTE_TOOLS = [
  { id: "tablero", to: "/app/tablero", icon: <IconLayoutDashboard size={22} />, label_es: "Control", label_en: "Control", sub_es: "Usuarios, accesos, ingresos, proyección y simulador.", sub_en: "Users, access, revenue, projection and simulator." },
  { id: "actividad", to: "/app/admin/actividad", icon: <IconReceipt size={22} />, label_es: "Compras & actividad", label_en: "Purchases & activity", sub_es: "Identidad de compradores, historial de compras y notificaciones del panel.", sub_en: "Buyer identity, purchase history and panel notifications." },
  { id: "usuarios", to: "/app/admin/usuarios", icon: <IconUsers size={22} />, label_es: "Usuarios", label_en: "Users", sub_es: "Buscar y administrar cuentas de familia, docente, sombra, escuela y psicóloga.", sub_en: "Search and manage family, teacher, shadow, school and psychologist accounts." },
  { id: "boletin", to: "/app/boletin", icon: <IconMail size={22} />, label_es: "Comunicación", label_en: "Communication", sub_es: "Arma el boletín semanal desde las señales de la red y apruébalo.", sub_en: "Build the weekly bulletin from network signals and approve it." },
  { id: "evidencia", to: "/app/evidencia", icon: <IconChartArrowsVertical size={22} />, label_es: "Impacto", label_en: "Impact", sub_es: "Liga el curso aprobado por el docente con la mejora de sus estudiantes.", sub_en: "Link approved teacher courses with student improvement." },
  { id: "historial", to: "/app/historial", icon: <IconHistory size={22} />, label_es: "Historial", label_en: "History", sub_es: "Ver y descargar CSV UTF-8 de registros conversacionales por módulo y rango de fechas.", sub_en: "View and download UTF-8 CSV of conversational records per module and date range." },
  { id: "newsletter", to: "/app/admin/newsletter", icon: <IconMail size={22} />, label_es: "Boletín & suscriptores", label_en: "Bulletin & subscribers", sub_es: "Gestiona suscriptores del Observatorio y envía el boletín semanal (martes) desde info@chiessainstitute.com.", sub_en: "Manage Observatory subscribers and send the weekly bulletin (Tuesdays) from info@chiessainstitute.com." },
  { id: "integraciones", to: "/app/admin/integraciones", icon: <IconPlug size={22} />, label_es: "Integraciones", label_en: "Integrations", sub_es: "Google Drive y almacenamiento externo para archivos clínicos.", sub_en: "Google Drive & external storage for clinical files." },
];

export default function AppHome() {
  const { t, lang } = useI18n();
  const { user, refreshMe, switchRole } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.is_admin || user?.role === "psicologa";
  const hasMultipleRoles = Array.isArray(user?.roles) && user.roles.length > 1;

  // Role switcher state
  const [switching, setSwitching] = useState(false);
  const doSwitch = async (r) => {
    if (r === user?.role) return;
    setSwitching(true);
    try {
      await switchRole(r);
      window.location.reload(); // refresh entitlements/RBAC-scoped UI cleanly
    } catch (e) { console.error(formatApiError(e)); }
    finally { setSwitching(false); }
  };

  // Inline gender picker for users that haven't set it yet (so we can address them properly)
  const [showGenderPicker, setShowGenderPicker] = useState(false);
  const [savingGender, setSavingGender] = useState(false);
  const saveGender = async (g) => {
    setSavingGender(true);
    try {
      await api.patch("/auth/me", { gender: g });
      if (refreshMe) await refreshMe();
      setShowGenderPicker(false);
    } catch (e) { console.error(formatApiError(e)); }
    finally { setSavingGender(false); }
  };
  const dismissGender = () => setShowGenderPicker(false);

  return (
    <div className="wrap-app max-w-[1160px] py-10">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <p className="eyebrow">{t("app.home_eye")}</p>
        <h1 className="font-display font-medium text-[clamp(28px,4vw,42px)] mt-2 leading-tight">
          {(() => {
            const first = user?.name?.split(" ")[0] || "";
            if (lang !== "es") return `Welcome, ${first}`;
            const g = user?.gender;
            const isEscuela = user?.role === "escuela";
            if (isEscuela) {
              // Trato formal de Usted para módulo Escuela
              // Skip auto title if name already starts with one (Dir./Dr./Dra./Lic./etc.)
              const startsWithTitle = /^(dr\.|dra\.|dir\.|dirª\.|lic\.|mtro\.|mtra\.|prof\.|ing\.|arq\.)/i.test(first);
              if (startsWithTitle) return `Le damos la bienvenida, ${first}`;
              const title = g === "femenino" ? "Dra." : g === "masculino" ? "Dr." : "";
              return title ? `Le damos la bienvenida, ${title} ${first}` : `Le damos la bienvenida, ${first}`;
            }
            if (g === "masculino") return `Bienvenido, ${first}`;
            if (g === "femenino") return `Bienvenida, ${first}`;
            // Neutral fallback for undefined
            return `Te damos la bienvenida, ${first}`;
          })()}
        </h1>
        <p className="text-muted text-[15px] mt-2">
          {user?.role === "escuela" && lang === "es"
            ? "Panel institucional para gestionar la práctica inclusiva de su centro."
            : t("app.home_lead")}
        </p>

        {/* Gender picker (only for logged-in users who haven't set it yet) */}
        {user && !user.gender && (
          showGenderPicker ? (
            <div
              className="mt-4 p-3 rounded-lg border border-teal-soft bg-teal-soft/30 flex items-center gap-3 flex-wrap max-w-[560px]"
              data-testid="app-gender-picker"
            >
              <IconUserCog size={16} className="text-teal-deep shrink-0" />
              <span className="text-[12.5px] font-medium">
                {lang === "es" ? (user?.role === "escuela" ? "¿Cómo desea que le tratemos?" : "¿Cómo te dirigimos?") : "How should we address you?"}
              </span>
              <button className="btn-ghost btn text-[11.5px]" onClick={() => saveGender("femenino")} disabled={savingGender} data-testid="app-gender-fem">
                {lang === "es" ? "Femenino" : "Female"}
              </button>
              <button className="btn-ghost btn text-[11.5px]" onClick={() => saveGender("masculino")} disabled={savingGender} data-testid="app-gender-masc">
                {lang === "es" ? "Masculino" : "Male"}
              </button>
              {savingGender && <IconLoader2 size={12} className="animate-spin" />}
              <button className="ml-auto text-tert hover:text-ink" onClick={dismissGender} aria-label="Cerrar">
                <IconX size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowGenderPicker(true)}
              className="mt-3 text-[12px] text-teal-deep hover:underline inline-flex items-center gap-1"
              data-testid="app-gender-prompt"
            >
              <IconUserCog size={12} />
              {lang === "es" ? (user?.role === "escuela" ? "Configure cómo desea que le tratemos" : "Configura cómo te dirigimos") : "Set how we address you"}
            </button>
          )
        )}

        {/* Role switcher — only when user has 2+ roles */}
        {hasMultipleRoles && (
          <div
            className="mt-4 p-3 rounded-lg border border-honey/50 bg-honey-soft/30 flex items-center gap-2 flex-wrap max-w-[720px]"
            data-testid="app-role-switcher"
          >
            <IconUserCog size={14} className="text-honey-deep shrink-0" />
            <span className="text-[12.5px] font-medium">
              {lang === "es" ? "Rol activo:" : "Active role:"}
            </span>
            {user.roles.map((r) => (
              <button
                key={r}
                onClick={() => doSwitch(r)}
                disabled={switching || r === user.role}
                className={`btn-ghost btn text-[11.5px] ${r === user.role ? "border border-teal-deep text-teal-deep font-medium" : ""}`}
                data-testid={`app-role-btn-${r}`}
              >
                {r === user.role && <IconCircleCheck size={11} className="text-teal-deep" />}
                {ROLE_LABELS[lang][r] || r}
              </button>
            ))}
            {switching && <IconLoader2 size={12} className="animate-spin" />}
          </div>
        )}
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {visibleModules(user, MODULES).map((m, i) => {
          const mod = m.id;
          return (
            <motion.div
              key={mod}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 * i }}
              className="mod-card"
              onClick={() => nav(m.to)}
              data-testid={`app-module-${mod}`}
            >
              {m.active && <span className="badge">{t("app.module_active")}</span>}
              <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal-deep flex items-center justify-center mb-3">
                {m.icon}
              </div>
              <span className="tag">{t(`modules.${mod}.name`)}</span>
              <h3 className="font-display text-[20px] font-medium mt-2">{t(`modules.${mod}.who`)}</h3>
              <p className="text-muted text-[13.5px] mt-2 leading-relaxed">{t(`modules.${mod}.desc`)}</p>
              <span className="inline-flex items-center gap-1 text-teal text-[12.5px] mt-4 font-medium">
                {t("app.enter")} <IconArrowRight size={13} />
              </span>
            </motion.div>
          );
        })}
      </div>

      {isAdmin && (
        <>
          <p className="eyebrow mt-14">{t("app.institute_tools")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
            {INSTITUTE_TOOLS.map((tool) => (
              <div
                key={tool.id}
                className="mod-card"
                onClick={() => nav(tool.to)}
                data-testid={`app-tool-${tool.id}`}
              >
                <span className="badge" style={{ background: "#EEEDFE", color: "#3C3489" }}>
                  {t("app.only_mariela")}
                </span>
                <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal-deep flex items-center justify-center mb-3">
                  {tool.icon}
                </div>
                <span className="tag" style={{ background: "#F1EFE9", color: "#5C5F55" }}>
                  {lang === "es"
                    ? (tool.id === "tablero" ? "Tablero" : tool.id === "actividad" ? "Actividad" : tool.id === "usuarios" ? "Usuarios" : tool.id === "boletin" ? "Boletín" : tool.id === "evidencia" ? "Evidencia" : tool.id === "newsletter" ? "Boletín" : tool.id === "integraciones" ? "Integraciones" : "Historial")
                    : (tool.id === "tablero" ? "Dashboard" : tool.id === "actividad" ? "Activity" : tool.id === "usuarios" ? "Users" : tool.id === "boletin" ? "Bulletin" : tool.id === "evidencia" ? "Evidence" : tool.id === "newsletter" ? "Newsletter" : tool.id === "integraciones" ? "Integrations" : "History")}
                </span>
                <h3 className="font-display text-[20px] font-medium mt-2">
                  {lang === "es" ? tool.label_es : tool.label_en}
                </h3>
                <p className="text-muted text-[13.5px] mt-2 leading-relaxed">
                  {lang === "es" ? tool.sub_es : tool.sub_en}
                </p>
                <span className="inline-flex items-center gap-1 text-teal text-[12.5px] mt-4 font-medium">
                  {t("app.open")} <IconArrowRight size={13} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Billing quick access — all authenticated users */}
      <div className="mt-14">
        <div
          className="mod-card cursor-pointer flex items-center justify-between gap-4 flex-wrap"
          onClick={() => nav("/app/billing")}
          data-testid="app-billing-link"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-honey-soft text-honey-deep flex items-center justify-center">
              <IconCreditCard size={22} />
            </div>
            <div>
              <p className="lbl m-0">{lang === "es" ? "Facturación" : "Billing"}</p>
              <p className="text-muted text-[13px] mt-0.5">
                {lang === "es"
                  ? "Planes, cursos y compras. Métodos: Stripe (activo), PayPal (próximamente)."
                  : "Plans, courses and purchases. Methods: Stripe (live), PayPal (soon)."}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-teal text-[12.5px] font-medium">
            {t("app.open")} <IconArrowRight size={13} />
          </span>
        </div>
      </div>
    </div>
  );
}
